import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid2 } from '@mui/material'
import Select from 'components/forms/Select'
import { groupBy, intersectionWith, isNil, isUndefined, orderBy, uniqBy } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import NumberField from 'components/forms/NumberField'
import useSandreList from 'utils/customHook/useSandreList'
import { nbPerPageLabel, nbPerPageLabelMedium } from 'referencial/constants/ReferencialConstants'
import { ParameterFilterField } from 'components/datatable/SimpleSelectionTable'
import { searchAllCharacters } from 'utils/StringUtil'
import useBoolean from 'utils/customHook/useBoolean'
import useListIndexed from 'utils/customHook/useListIndexed'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { CardTable, NewTable } from 'components/datatable/NewTable'
import { THRESHOLD_DISCRIMINANT, THRESHOLD_DISPLAY, THRESHOLD_TYPE } from 'utils/QualityThresholdUtils'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import SliderPanel from 'components/slider/SliderPanel'

const PopinParameterThreshold = ({
    isOpen = false,
    fullyUpdatable = false,
    parameterThreshold = {},
    validate = () => {},
    close = () => {},
}) => {
    const {
        units,
        parameters,
        qualitometers,
        fractions,
        parametersIndex,
        unitsIndex,
    } = useSelector(store => ({
        units: store.UnitReducer.units,
        parameters: store.ParameterReducer.parameters,
        qualitometers: store.QualityReducer.qualitometersLight,
        fractions: store.FractionReducer.fractions,
        parametersIndex: store.ParameterReducer.parametersIndex,
        unitsIndex: store.UnitReducer.unitsIndex,
    }), shallowEqual)

    const lexiconCalcium = useSandreList('CALCIUM')
    const lexiconFRType = useSandreList('QUALITOMETRES.TYPE_FR.RIVIERE')
    const lexiconFishContext = useSandreList('QUALITOMETRES.CONTEXTEPISCICOLE')

    const [parameterCode, setParameterCode] = useState()
    const [unit, setUnit] = useState()
    const [station, setStation] = useState()
    const [fraction, setFraction] = useState()

    const [threshold1, setThreshold1] = useState()
    const [threshold2, setThreshold2] = useState()
    const [threshold3, setThreshold3] = useState()
    const [threshold4, setThreshold4] = useState()

    const [calciumClass, setCalciumClass] = useState()
    const [frType, setFrType] = useState()
    const [fishCode, setFishCode] = useState()

    const [thresholdType, setThresholdType] = useState()
    const [display, setDisplay] = useState()
    const [discriminant, setDiscriminant] = useState()

    useEffect(() => {
        if (isOpen) {
            setParameterCode(parameterThreshold.parameterCode)
            setUnit(parameterThreshold.unit)
            setStation(parameterThreshold.station)
            setFraction(parameterThreshold.fraction)

            setThreshold1(parameterThreshold.threshold1)
            setThreshold2(parameterThreshold.threshold2)
            setThreshold3(parameterThreshold.threshold3)
            setThreshold4(parameterThreshold.threshold4)

            setCalciumClass(parameterThreshold.calciumClass)
            setFrType(parameterThreshold.frType)
            setFishCode(parameterThreshold.fishCode)

            setThresholdType(parameterThreshold.thresholdType ?? THRESHOLD_TYPE.INCREASING)
            setDisplay(parameterThreshold.display ?? THRESHOLD_DISPLAY.INCREASING)
            setDiscriminant(parameterThreshold.discriminant ?? THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL)
        }
    }, [isOpen, parameterThreshold.calciumClass, parameterThreshold.discriminant, parameterThreshold.display, parameterThreshold.fishCode, parameterThreshold.frType, parameterThreshold.fraction, parameterThreshold.parameterCode, parameterThreshold.station, parameterThreshold.threshold1, parameterThreshold.threshold2, parameterThreshold.threshold3, parameterThreshold.threshold4, parameterThreshold.thresholdType, parameterThreshold.unit])

    useEffect(() => {
        if (isUndefined(threshold1)) {
            setThreshold2(undefined)
            setThreshold3(undefined)
            setThreshold4(undefined)
        }
    }, [threshold1])

    useEffect(() => {
        if (isUndefined(threshold2)) {
            setThreshold3(undefined)
            setThreshold4(undefined)
        }
    }, [threshold2])

    useEffect(() => {
        if (isUndefined(threshold3)) {
            setThreshold4(undefined)
        }
    }, [threshold3])

    const onChangeThresholdType = newType => {
        setThresholdType(newType)
        if (newType === THRESHOLD_TYPE.BOUNDED) {
            setDiscriminant(THRESHOLD_DISCRIMINANT.INF_OR_SUP_OR_EQUAL)
            setDisplay(THRESHOLD_DISPLAY.INCREASING)
            setThreshold3()
            setThreshold4()
            if (thresholdType === THRESHOLD_TYPE.DECREASING) {
                setThreshold1(threshold2)
                setThreshold2(threshold1)
            }
        }
        if (newType === THRESHOLD_TYPE.INCREASING) {
            setDiscriminant(THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL)
            if (thresholdType === THRESHOLD_TYPE.DECREASING) {
                const [th1, th2, th3, th4] = [threshold1, threshold2, threshold3, threshold4].filter(th => !isNil(th)).toReversed()
                setThreshold1(th1)
                setThreshold2(th2)
                setThreshold3(th3)
                setThreshold4(th4)
            }
        }
        if (newType === THRESHOLD_TYPE.DECREASING) {
            setDiscriminant(THRESHOLD_DISCRIMINANT.INF_OR_EQUAL)
            const [th1, th2, th3, th4] = [threshold1, threshold2, threshold3, threshold4].filter(th => !isNil(th)).toReversed()
            setThreshold1(th1)
            setThreshold2(th2)
            setThreshold3(th3)
            setThreshold4(th4)
        }
    }

    const thresholdTypes = useMemo(() => [
        { id: THRESHOLD_TYPE.INCREASING, name: i18n.increasing },
        { id: THRESHOLD_TYPE.DECREASING, name: i18n.decreasing },
        { id: THRESHOLD_TYPE.BOUNDED, name: i18n.bounded },
        // { id: THRESHOLD_TYPE.INVERTED_BOUNDED, name: 'Borné inversé' },
    ], [])

    const displayModes = useMemo(() => [
        { id: THRESHOLD_DISPLAY.INCREASING, name: i18n.increasing },
        { id: THRESHOLD_DISPLAY.DECREASING, name: i18n.decreasing },
    ], [])

    const increasingDiscriminants = useMemo(() => [
        { id: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL, name: `x >= ${i18n.threshold}` },
        { id: THRESHOLD_DISCRIMINANT.STRICTLY_SUP, name: `x > ${i18n.threshold}` },
    ], [])

    const decreasingDiscriminants = useMemo(() => [
        { id: THRESHOLD_DISCRIMINANT.INF_OR_EQUAL, name: `x <= ${i18n.threshold}` },
        { id: THRESHOLD_DISCRIMINANT.STRICTLY_INF, name: `x < ${i18n.threshold}` },
    ], [])

    const boundedDiscriminants = useMemo(() => [
        { id: THRESHOLD_DISCRIMINANT.INF_OR_SUP_OR_EQUAL, name: `${i18n.threshold} 1 <= x ${i18n.and} ${i18n.threshold} 2 >= x` },
        { id: THRESHOLD_DISCRIMINANT.STRICTLY_INF_OR_SUP, name: `${i18n.threshold} 1 < x ${i18n.and} ${i18n.threshold} 2 > x` },
    ], [])

    const usedDiscriminants = useMemo(() => {
        if (thresholdType === THRESHOLD_TYPE.INCREASING) return increasingDiscriminants
        if (thresholdType === THRESHOLD_TYPE.DECREASING) return decreasingDiscriminants
        if (thresholdType === THRESHOLD_TYPE.BOUNDED) return boundedDiscriminants
        return []
    }, [boundedDiscriminants, decreasingDiscriminants, increasingDiscriminants, thresholdType])

    return (
        <Dialog
            maxWidth='lg'
            fullWidth
            open={isOpen}
            slotProps={{
                paper: {
                    sx: {
                        minHeight: undefined,
                        maxHeight: undefined,
                    },
                },
            }}
        >
            <DefaultDialogTitle
                title={i18n.threshold}
                onClose={close}
            />
            <DialogContent style={{ overflowX: 'hidden' }}>
                <Grid2 container spacing={1} direction='column'>
                    <Grid2>
                        <Card>
                            <CardContent>
                                <Grid2 container columnSpacing={2} rowSpacing={1} alignItems='center'>
                                    <Grid2 size={6}>
                                        <Select
                                            label={i18n.parameter}
                                            value={parameterCode}
                                            onChange={setParameterCode}
                                            options={parameters}
                                            disabled={!fullyUpdatable}
                                            keyLabel='labelWithCode'
                                        />
                                    </Grid2>
                                    <Grid2 size={6}>
                                        <Select
                                            label={i18n.unit}
                                            value={unit}
                                            onChange={setUnit}
                                            options={units}
                                            disabled={!fullyUpdatable}
                                            keyLabel='symbolWithCode'
                                        />
                                    </Grid2>
                                    <Grid2 size={6}>
                                        <Select
                                            label={i18n.station}
                                            value={station}
                                            onChange={setStation}
                                            options={qualitometers}
                                            disabled={!fullyUpdatable}
                                            keyValue='id'
                                            keyLabel='displayLabel'
                                        />
                                    </Grid2>
                                    <Grid2 size={6}>
                                        <Select
                                            label={i18n.fraction}
                                            value={fraction}
                                            onChange={setFraction}
                                            options={fractions}
                                            disabled={!fullyUpdatable}
                                            keyValue='id'
                                            keyLabel='labelWithCode'
                                        />
                                    </Grid2>


                                    <Grid2 size={4}>
                                        <SuperMultiAutocomplete
                                            label={i18n.thresholdType}
                                            values={thresholdType}
                                            onChange={onChangeThresholdType}
                                            options={thresholdTypes}
                                            noNullValue
                                        />
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <SuperMultiAutocomplete
                                            label={i18n.displayMode}
                                            values={display}
                                            onChange={setDisplay}
                                            options={displayModes}
                                            disabled={thresholdType === THRESHOLD_TYPE.BOUNDED}
                                            noNullValue
                                        />
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <SuperMultiAutocomplete
                                            label={i18n.discriminant}
                                            values={discriminant}
                                            onChange={setDiscriminant}
                                            options={usedDiscriminants}
                                            disabled={thresholdType === THRESHOLD_TYPE.BOUNDED}
                                            noNullValue
                                        />
                                    </Grid2>

                                    <Grid2 size={3}>
                                        <NumberField
                                            title={i18n.threshold1}
                                            value={threshold1}
                                            floatValue
                                            onChange={setThreshold1}
                                            obligatory
                                            min={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold2 : undefined}
                                            max={thresholdType === THRESHOLD_TYPE.DECREASING ? undefined : threshold2}
                                        />
                                    </Grid2>
                                    <Grid2 size={3}>
                                        {
                                            (!isUndefined(threshold1) || thresholdType === THRESHOLD_TYPE.BOUNDED) && (
                                                <NumberField
                                                    title={i18n.threshold2}
                                                    value={threshold2}
                                                    floatValue
                                                    onChange={setThreshold2}
                                                    obligatory={thresholdType === THRESHOLD_TYPE.BOUNDED}
                                                    min={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold3 : threshold1}
                                                    max={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold1 : threshold3}
                                                />
                                            )
                                        }
                                    </Grid2>
                                    <Grid2 size={3}>
                                        {
                                            !isUndefined(threshold2) && thresholdType !== THRESHOLD_TYPE.BOUNDED && (
                                                <NumberField
                                                    title={i18n.threshold3}
                                                    value={threshold3}
                                                    floatValue
                                                    onChange={setThreshold3}
                                                    min={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold4 : threshold2}
                                                    max={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold2 : threshold4}
                                                />
                                            )
                                        }
                                    </Grid2>
                                    <Grid2 size={3}>
                                        {
                                            !isUndefined(threshold3) && thresholdType !== THRESHOLD_TYPE.BOUNDED && (
                                                <NumberField
                                                    title={i18n.threshold4}
                                                    value={threshold4}
                                                    floatValue
                                                    onChange={setThreshold4}
                                                    min={thresholdType === THRESHOLD_TYPE.DECREASING ? undefined : threshold3}
                                                    max={thresholdType === THRESHOLD_TYPE.DECREASING ? threshold3 : undefined}
                                                />
                                            )
                                        }
                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Select
                                            label={i18n.hardness}
                                            value={calciumClass}
                                            options={lexiconCalcium}
                                            onChange={setCalciumClass}
                                        />
                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Select
                                            label={i18n.frType}
                                            value={frType}
                                            options={lexiconFRType}
                                            onChange={setFrType}
                                        />
                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Select
                                            label={i18n.fishContext}
                                            value={fishCode}
                                            options={lexiconFishContext}
                                            onChange={setFishCode}
                                        />
                                    </Grid2>
                                </Grid2>
                            </CardContent>
                        </Card>
                    </Grid2>
                    {!isNil(threshold1) && (thresholdType !== THRESHOLD_TYPE.BOUNDED || !isNil(threshold2)) && (
                        <Grid2>
                            <Card>
                                <CardContent>
                                    <SliderPanel
                                        title={parametersIndex[parameterCode]?.labelWithCode ?? i18n.defaultThreshold}
                                        subtitle={unitsIndex[unit]?.symbolWithCode}
                                        analysis={{ parameter: parameterCode, unit, result: 0, remark: '1' }}
                                        threshold={{
                                            thresholdType,
                                            display,
                                            discriminant,
                                            threshold1,
                                            threshold2,
                                            threshold3,
                                            threshold4,
                                        }}
                                        withThresholdLevels
                                    />
                                </CardContent>
                            </Card>
                        </Grid2>
                    )}
                </Grid2>
            </DialogContent>
            <DialogActions>
                <Grid2 container alignItems='center' justifyContent='right' columnSpacing={2} style={{ padding: '0 10' }}>
                    <Grid2 container size={3} justifyContent='flex-end'>
                        <Button
                            onClick={() => {
                                validate({
                                    parameterCode,
                                    unit,
                                    station,
                                    fraction,
                                    threshold1,
                                    threshold2,
                                    threshold3,
                                    threshold4,
                                    calciumClass,
                                    frType,
                                    fishCode,
                                    thresholdType,
                                    display,
                                    discriminant,
                                })
                            }}
                            variant='contained'
                            color='primary'
                            disabled={isUndefined(threshold1)}
                        >
                            {i18n.validate}
                        </Button>
                    </Grid2>
                </Grid2>
            </DialogActions>
        </Dialog>
    )
}

PopinParameterThreshold.propTypes = {
    isOpen: PropTypes.bool,
    fullyUpdatable: PropTypes.bool,
    threshold: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
    }),
    parameterThreshold: PropTypes.shape({
        parameterCode: PropTypes.string,
        unit: PropTypes.string,
        station: PropTypes.number,
        fraction: PropTypes.string,
        threshold1: PropTypes.number,
        threshold2: PropTypes.number,
        threshold3: PropTypes.number,
        threshold4: PropTypes.number,
        calciumClass: PropTypes.number,
        frType: PropTypes.number,
        fishCode: PropTypes.number,
    }),
    validate: PropTypes.func,
    close: PropTypes.func,
}

const StepParameterThresholds = ({
    qualityThresholds = [],
    setQualityThresholds = () => {},
}) => {
    const {
        parameters,
        units,
        qualitometers,
        fractions,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        qualitometers: store.QualityReducer.qualitometersLight,
        fractions: store.FractionReducer.fractions,
    }), shallowEqual)

    const indexedParameters = useListIndexed(parameters, 'code')
    const indexedUnits = useListIndexed(units, 'code')
    const indexedQualitometers = useListIndexed(qualitometers, 'id')
    const indexedFractions = useListIndexed(fractions, 'code')

    const lexiconCalcium = useSandreList('CALCIUM')
    const lexiconFRType = useSandreList('QUALITOMETRES.TYPE_FR.RIVIERE')
    const lexiconFishContext = useSandreList('QUALITOMETRES.CONTEXTEPISCICOLE')

    const [filter, setFilter] = useState({})
    const [selectedIndex, setSelectedIndex] = useState()
    const {
        value: isOpen,
        setTrue: open,
        setFalse: close,
    } = useBoolean(false)

    const selectedThreshold = useMemo(() => {
        return qualityThresholds[selectedIndex]
    }, [qualityThresholds, selectedIndex])

    const groupThresholds= groupBy(qualityThresholds, th => `${th.parameterCode}:${th.unit}:${th.station}:${th.fraction}`)

    const formattedThresholds = useMemo(() => {
        return qualityThresholds.map((th, index) => {
            const key = `${th.parameterCode}:${th.unit}:${th.station}:${th.fraction}`
            const parameter = indexedParameters[th.parameterCode]

            return {
                code: th.parameterCode,
                parameter: isUndefined(th.parameterCode) ? i18n.defaultThreshold : parameter?.displayLabel,
                unit: indexedUnits[th.unit]?.symbolWithCode ?? '',
                station: indexedQualitometers[th.station]?.displayLabel,
                fraction: indexedFractions[th.fraction]?.labelWithCode,
                threshold1: th.threshold1,
                threshold2: th.threshold2,
                threshold3: th.threshold3,
                threshold4: th.threshold4,
                hardness: lexiconCalcium.find(l => l.code === th.calciumClass)?.name,
                frType: lexiconFRType.find(l => l.code === th.frType)?.name,
                fishContext: lexiconFishContext.find(l => l.code === th.fishCode)?.name,
                colorLine: groupThresholds[key].length > 1 ? '#ff8c86' : '#fff',
                index,
                searchValue: [parameter?.name, parameter?.shortLabel, parameter?.code].map((label = '') => searchAllCharacters(label)),
            }
        })
    }, [groupThresholds, indexedFractions, indexedParameters, indexedQualitometers, indexedUnits, lexiconCalcium, lexiconFRType, lexiconFishContext, qualityThresholds])


    const filteredThresholds = useMemo(() => {
        const { selection = '-1', listParam, searchValue } = filter
        const filterSelection = selection !== '-1' ? intersectionWith(formattedThresholds, listParam, (a, code) => a.code === code) : formattedThresholds
        const searchValueFormat = searchAllCharacters(searchValue)
        return searchValue ? filterSelection.filter(p => p.searchValue.some(v => v.includes(searchValueFormat))) : filterSelection
    }, [filter, formattedThresholds])

    const actions = [{
        icon: 'note_add',
        tooltip: i18n.add,
        onClick: () => {
            setSelectedIndex()
            open()
        },
    }, {
        icon: 'info',
        tooltip: i18n.redLineMeanDuplicate,
    }]

    const nbThresholds = qualityThresholds.length
    const nbParameters = uniqBy(qualityThresholds, 'parameterCode').length

    return (
        <Grid2 container justifyContent='center' rowSpacing={1}>
            <Grid2 size={12}>
                <Card>
                    <CardContent>
                        <ParameterFilterField
                            filter={filter}
                            setFilter={setFilter}
                        />
                    </CardContent>
                </Card>
            </Grid2>
            <Grid2 size={12}>
                <CardTable
                    title={`${nbThresholds} ${nbThresholds > 1 ? i18n.thresholds : i18n.threshold}, ${nbParameters} ${nbParameters > 1 ? i18n.parameters : i18n.parameter}`}
                    actions={actions}
                    hideNbElements

                    headers={['parameter', 'unit', 'station', 'fraction', 'threshold1', 'threshold2', 'threshold3', 'threshold4', 'hardness', 'frType', 'fishContext']}
                    rows={orderBy(filteredThresholds, 'parameter')}
                    rowsPerPageOptions={nbPerPageLabel}

                    lineActions={[
                        {
                            icon: 'delete',
                            onClick: ({ index }) => setQualityThresholds(list => list.filter((_, i) => i !== index)),
                        },
                        {
                            icon: 'edit',
                            onClick: ({ index }) => {
                                setSelectedIndex(index)
                                open()
                            },
                        },
                        {
                            icon: 'queue',
                            onClick: ({ index }) => setQualityThresholds(list => [...list, qualityThresholds[index]]),
                        },
                    ]}
                />
                <PopinParameterThreshold
                    isOpen={isOpen}
                    fullyUpdatable
                    parameterThreshold={selectedThreshold}
                    validate={newThreshold => {
                        setQualityThresholds(list => {
                            if (isUndefined(selectedIndex)) {
                                return [...list, newThreshold]
                            }
                            return list.map((th, i) => i === selectedIndex ? newThreshold : th)
                        })
                        close()
                    }}
                    close={close}
                />
            </Grid2>
        </Grid2>
    )
}

StepParameterThresholds.propTypes = {
    threshold: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
    }),
    qualityThresholds: PropTypes.arrayOf(PropTypes.shape({
        parameterCode: PropTypes.string,
        unit: PropTypes.string,
        station: PropTypes.number,
        fraction: PropTypes.string,
        threshold1: PropTypes.number,
        threshold2: PropTypes.number,
        threshold3: PropTypes.number,
        threshold4: PropTypes.number,
        calciumClass: PropTypes.number,
        frType: PropTypes.number,
        fishCode: PropTypes.number,
    })),
    setQualityThresholds: PropTypes.func,
}

const TableParameterThreshold = ({
    threshold = {},
    qualityThresholds = [],
    setQualityThresholds = () => {},
    isEditMode = false,
}) => {
    const {
        parameters,
        units,
        qualitometers,
        fractions,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        qualitometers: store.QualityReducer.qualitometersLight,
        fractions: store.FractionReducer.fractions,
    }), shallowEqual)

    const parametersIndexed = useListIndexed(parameters, 'code')
    const unitsIndexed = useListIndexed(units, 'code')
    const indexedQualitometers = useListIndexed(qualitometers, 'id')
    const indexedFractions = useListIndexed(fractions, 'code')

    const lexiconCalcium = useSandreList('CALCIUM')
    const lexiconFRType = useSandreList('QUALITOMETRES.TYPE_FR.RIVIERE')
    const lexiconFishContext = useSandreList('QUALITOMETRES.CONTEXTEPISCICOLE')

    const [selectedIndex, setSelectedIndex] = useState()
    const {
        value: isOpen,
        setTrue: open,
        setFalse: close,
    } = useBoolean(false)

    const selectedThreshold = useMemo(() => {
        return qualityThresholds[selectedIndex]
    }, [qualityThresholds, selectedIndex])

    const thresholdsFormatted = useMemo(() => {
        return qualityThresholds?.map((th, index) => ({
            code: th.parameterCode,
            parameter: parametersIndexed[th.parameterCode]?.labelWithCode ?? '',
            unit: unitsIndexed[th.unit]?.symbol ?? '',
            station: indexedQualitometers[th.station]?.displayLabel,
            fraction: indexedFractions[th.fraction]?.labelWithCode,
            threshold1: th.threshold1,
            threshold2: th.threshold2,
            threshold3: th.threshold3,
            threshold4: th.threshold4,
            hardness: lexiconCalcium.find(l => l.code === th.calciumClass)?.name,
            frType: lexiconFRType.find(l => l.code === th.frType)?.name,
            fishContext: lexiconFishContext.find(l => l.code === th.fishCode)?.name,
            index,
        }))
    }, [indexedFractions, indexedQualitometers, lexiconCalcium, lexiconFRType, lexiconFishContext, parametersIndexed, qualityThresholds, unitsIndexed])

    return (
        <>
            <NewTable
                headers={['parameter', 'unit', 'station', 'fraction', 'threshold1', 'threshold2', 'threshold3', 'threshold4', 'hardness', 'frType', 'fishContext']}
                rows={thresholdsFormatted}
                rowsPerPageOptions={nbPerPageLabelMedium}

                lineActions={[
                    {
                        icon: 'delete',
                        onClick: ({ index }) => setQualityThresholds(threshold.code, qualityThresholds.filter((_, i) => i !== index)),
                        displayed: isEditMode,
                    },
                    {
                        icon: 'edit',
                        onClick: ({ index }) => {
                            setSelectedIndex(index)
                            open()
                        },
                        displayed: isEditMode,
                    },
                ]}
            />
            <PopinParameterThreshold
                isOpen={isOpen}
                parameterThreshold={selectedThreshold}
                validate={newThreshold => {
                    const newQualityThresholds = qualityThresholds.map((th, i) => i === selectedIndex ? newThreshold : th)
                    setQualityThresholds(threshold.code, newQualityThresholds)
                    close()
                }}
                close={close}
            />
        </>
    )
}

TableParameterThreshold.propTypes = {
    threshold: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
    }),
    qualityThresholds: PropTypes.arrayOf(PropTypes.shape({
        parameterCode: PropTypes.string,
        unit: PropTypes.string,
        station: PropTypes.number,
        fraction: PropTypes.string,
        threshold1: PropTypes.number,
        threshold2: PropTypes.number,
        threshold3: PropTypes.number,
        threshold4: PropTypes.number,
        calciumClass: PropTypes.number,
        frType: PropTypes.number,
        fishCode: PropTypes.number,
    })),
    setQualityThresholds: PropTypes.func,
    isEditMode: PropTypes.bool,
}

export {
    PopinParameterThreshold,
    TableParameterThreshold,
    StepParameterThresholds,
}